/**
 * Routines (docs/plans/routines.md): what RecordOS does by itself, from a
 * schedule or a date. Three kinds, each kept where it always was (R3):
 *
 *   bill   a charge plan's monthly run (`app.posting_rule`, pattern
 *          'charge_plan'), with the reminder the day before it
 *   task   a task rule of a record (`app.record.prefs.tasks`)
 *   email  a Gmail rule (`app.connector_rule`), seen only by its owner
 *
 * `GET /v1/routines` lists every routine the reader may see (R10, R11), each
 * with its sentence as parts: names and keys, never finished words, so every
 * client says one routine the same way through `routineWords` (R4). Pure.
 */
import type { ChargeCadence, ChargeRunStatus, RecordEntryNoun } from './api';
import { ruleWhenWords, type RulePausedReason, type RuleWhen } from './connectors';
import { t, tOr } from './i18n/t';
import type { TaskRuleWords } from './tasks';

export type RoutineKind = 'bill' | 'task' | 'email';

/** On; Paused (a monthly run a person paused, an email rule turned off); Needs attention. */
export type RoutineState = 'on' | 'paused' | 'attention';

/** Why a routine needs attention: its last run failed, or an email rule that is on cannot run. */
export type RoutineAttention =
  | { readonly kind: 'run_failed'; readonly periodStart: string }
  | { readonly kind: 'email_paused'; readonly reason: RulePausedReason };

/** The word for one party of a list: the person's own word (Customize) when set, else the list's noun key. */
export type RoutineNoun = { readonly nounKey: string; readonly noun: RecordEntryNoun | null };

/** One routine's sentence, as the server knows it: names and keys, said by `routineWords`. */
export type RoutineParts =
  | {
      readonly kind: 'bill';
      /** The party list's word for one row: "member". */
      readonly party: RoutineNoun;
      /** The party list's name: "Members", said when its word for one row is only the generic "entry". */
      readonly partyName: string;
      /** The label of the field the plan bills (its first amount); null when the amount is fixed. */
      readonly field: string | null;
      readonly cadence: ChargeCadence;
      /** The record the run writes into: "Membership fees". */
      readonly record: string;
    }
  | {
      readonly kind: 'task';
      /** The rule in names, as `GET /v1/records/:recordId/task-rules` says it. */
      readonly words: TaskRuleWords;
      /** The record's word for one entry. */
      readonly entry: RoutineNoun;
      readonly record: string;
    }
  | {
      readonly kind: 'email';
      readonly name: string;
      readonly when: RuleWhen;
      readonly record: string;
      /** The name of the person whose Gmail it sends from. */
      readonly owner: string;
    };

/** A monthly run a person paused (R7): who, and the workspace's day they paused it. */
export type RoutinePaused = { readonly byId: string; readonly by: string; readonly on: string };

/** One routine, as `GET /v1/routines` lists it. */
export type RoutineListItem = {
  readonly kind: RoutineKind;
  /** The charge plan's rule id, the task rule's id (unique within its record), or the Gmail rule's id. */
  readonly id: string;
  /** The record it writes to: a bill's charges record, a task rule's record, an email rule's record. */
  readonly recordId: string;
  readonly recordName: string;
  readonly parts: RoutineParts;
  readonly state: RoutineState;
  readonly attention: RoutineAttention | null;
  /** A bill: the paused flag; null on every other routine and on a bill that runs. */
  readonly paused: RoutinePaused | null;
  /** When it next runs: a bill's run day (`YYYY-MM-DD`), an email schedule's next slot (ISO 8601); null when it runs on a save. */
  readonly next: string | null;
  /** A task rule's open tasks; null on the other kinds. */
  readonly open: number | null;
  /**
   * What it did last: a bill's latest run (`at` is its period start), a task
   * rule's latest task or an email rule's latest run (ISO 8601); null when it
   * has not run.
   */
  readonly lastRun: { readonly at: string; readonly status: ChargeRunStatus } | null;
};

/** GET /v1/routines. */
export type RoutineListResponse = { readonly routines: readonly RoutineListItem[] };

/** POST /v1/records/:recordId/charge-runs/pause and /resume: the plan's paused flag after the call. */
export type ChargeRunPauseResponse = { readonly paused: RoutinePaused | null };

/**
 * A routine's state: Needs attention when something stopped it that a person
 * did not choose (a failed run, an email rule that cannot send), else Paused
 * when a person paused it, else On.
 */
export const routineStateOf = (args: { readonly paused: boolean; readonly attention: RoutineAttention | null }): RoutineState =>
  args.attention !== null ? 'attention' : args.paused ? 'paused' : 'on';

/* ---------------------------------------------------------------- the words */

/** A routine said in words: its name, and the line under it. */
export type RoutineWords = { readonly name: string; readonly line: string };

const joined = (parts: readonly string[]): string => parts.reduce((left, right) => t('routine.line', { left, right }));

/** One party, mid-sentence: the person's own word lowered, the noun key's, or "entry". */
const nounOne = (noun: RoutineNoun): string => {
  const own = noun.noun?.one.trim() ?? '';
  if (own !== '') return own.toLocaleLowerCase();
  return tOr(noun.nounKey, t('noun.entry', { count: 1 }), { count: 1 });
};

const startsWithVowel = (word: string): boolean => /^[aeiou]/i.test(word.trim());

const BILL_WHEN: Readonly<Record<ChargeCadence, () => string>> = {
  monthly: () => t('routine.bill.when_monthly'),
  quarterly: () => t('routine.bill.when_quarterly'),
  yearly: () => t('routine.bill.when_yearly'),
};

/** A list whose word for one row is only the generic "entry": no word of its own, and the generic or an unknown noun key. */
const isGenericNoun = (noun: RoutineNoun): boolean =>
  (noun.noun?.one.trim() ?? '') === '' && (noun.nounKey === 'noun.entry' || tOr(noun.nounKey, '', { count: 1 }) === '');

const billName = (parts: Extract<RoutineParts, { kind: 'bill' }>): string => {
  // "Bill each entry" says nothing: a list with no word of its own is named instead, "Bill everyone in Members".
  const list = parts.partyName.trim();
  if (list !== '' && isGenericNoun(parts.party)) {
    return parts.field === null ? t('routine.bill.what_list_plain', { list }) : t('routine.bill.what_list', { list, field: parts.field });
  }
  const party = nounOne(parts.party);
  return parts.field === null ? t('routine.bill.what_plain', { party }) : t('routine.bill.what', { party, field: parts.field });
};

const billWords = (parts: Extract<RoutineParts, { kind: 'bill' }>): RoutineWords => ({
  name: billName(parts),
  line: joined([BILL_WHEN[parts.cadence](), t('routine.bill.into', { record: parts.record })]),
});

const whoWords = (who: TaskRuleWords['who']): string => {
  switch (who.kind) {
    case 'owner':
      return t('routine.task.who.owner');
    case 'writer':
      return t('routine.task.who.writer');
    case 'role':
      return t('routine.task.who.role', { role: who.name });
    case 'field':
      return t('routine.task.who.field', { field: who.name });
  }
};

/** When a task rule raises its task: counted from a date field when it is due by one, else on the save that fires it. */
const taskWhen = (words: TaskRuleWords, entry: string): string => {
  const { from, days } = words.due;
  if (from !== null) {
    const count = Math.abs(days);
    if (days === 0) return t('routine.task.due_on', { field: from });
    return days < 0 ? t('routine.task.due_before', { count, field: from }) : t('routine.task.due_after', { count, field: from });
  }
  const an = startsWithVowel(entry);
  if (words.on === 'approve') return an ? t('routine.task.when_approved_an', { entry }) : t('routine.task.when_approved_a', { entry });
  return an ? t('routine.task.when_added_an', { entry }) : t('routine.task.when_added_a', { entry });
};

/** The task's own words, braces dropped: "Call Member". A title that starts in lower case reads as "A task to call the member". */
const taskName = (title: string): string => {
  const said = title.replace(/\{([^{}]+)\}/g, '$1').trim();
  const first = said.charAt(0);
  return first !== '' && first === first.toLocaleLowerCase() && first !== first.toLocaleUpperCase() ? t('routine.task.what', { title: said }) : said;
};

const taskWords = (parts: Extract<RoutineParts, { kind: 'task' }>): RoutineWords => ({
  name: taskName(parts.words.title),
  line: joined([taskWhen(parts.words, nounOne(parts.entry)), t('routine.task.on', { record: parts.record }), t('routine.task.for', { who: whoWords(parts.words.who) })]),
});

const emailWords = (parts: Extract<RoutineParts, { kind: 'email' }>, viewerZone: string | null): RoutineWords => ({
  name: parts.name,
  // The list says the schedule without the rule's condition: the rule's own page says it whole.
  line: joined([ruleWhenWords(parts.when, { record: parts.record, groups: [], viewerZone }), t('routine.email.from', { owner: parts.owner })]),
});

/**
 * A routine's name and the line under it (R4): "Bill each member their
 * Monthly fee", "On the 1st of every month · into Membership fees".
 * `viewerZone` is the reader's time zone: an email schedule in another one
 * names its zone; absent, the schedule is said in its own.
 */
export const routineWords = (item: Pick<RoutineListItem, 'parts'>, viewerZone?: string | null): RoutineWords => {
  const { parts } = item;
  switch (parts.kind) {
    case 'bill':
      return billWords(parts);
    case 'task':
      return taskWords(parts);
    case 'email':
      return emailWords(parts, viewerZone === undefined ? (parts.when.kind === 'schedule' ? parts.when.zone : null) : viewerZone);
  }
};
